import apiClient from '@/lib/api';

// The Emails page: every email the product sends, rendered server-side with
// made-up sample data (back-end-next/src/lib/email-previews/catalog.ts).

export type EmailSender = 'roasform' | 'ghl' | 'gleap' | 'baremetrics';

/** Whether it goes out today: live, built but its job not switched on, the AI pilot, or sent once per new version. */
export type EmailStatus = 'live' | 'not_switched_on' | 'pilot' | 'once';

/** Emails sent in versions (A, B, ...) share a group, which the page lists once. */
export interface EmailVersionGroup {
  id: string;
  name: string;
  /** One line on who gets which version. */
  versions: string;
}

export interface EmailPreview {
  id: string;
  name: string;
  category: string;
  sender: EmailSender;
  /** When it goes out, in plain words. */
  trigger: string;
  /** Who gets it. */
  recipient: string;
  status: EmailStatus;
  /** Its group of versions and which version it is; null for an email with one version. */
  group: EmailVersionGroup | null;
  version: string | null;
  /** Null when another service writes the email: the note says where it lives. */
  subject: string | null;
  html: string | null;
  note: string | null;
}

/** The periods the page offers, in days. */
export const STATS_PERIODS = [7, 30, 90] as const;
export type StatsPeriod = (typeof STATS_PERIODS)[number];

/**
 * How a tracked email (a trial or quiet email) did in the period
 * (back-end-next/src/lib/email-stats.ts). Opened reads high: Apple Mail opens every email.
 */
export interface EmailStat {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  /** Sends old enough for their whole did-it window. */
  didItEligible: number;
  /** Null when it can't be measured. */
  didIt: number | null;
  didItLabel: string;
  didItDays: number;
}

export const emailsService = {
  async list(): Promise<EmailPreview[]> {
    const res = await apiClient.get('/admin/emails');
    return res.data.data.emails;
  },
  /** Keyed by email id; emails we don't track aren't in it. */
  async stats(days: StatsPeriod): Promise<Record<string, EmailStat>> {
    const res = await apiClient.get('/admin/emails/stats', { params: { days } });
    return res.data.data.stats;
  },
};
